import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
  BulkImportCollectorsCsvAliasCollision,
  BulkImportCollectorsCsvInvalidRow,
  BulkImportCollectorsCsvPreviewResult,
  BulkImportCollectorsCsvResult,
  BulkImportCollectorsResult,
  CollectorsConfig
} from '../../domain/collector-profile.model';
import { AlertService } from '../../services/alert.service';
import { CollectorProfileService } from '../../services/collector-profile.service';

type BulkImportCollectorsMode = 'emails' | 'csv';

interface BulkImportCollectorsDialogData {
  config: CollectorsConfig;
}

@Component({
  selector: 'app-bulk-import-collectors-dialog',
  templateUrl: './bulk-import-collectors-dialog.component.html',
  styleUrls: ['./bulk-import-collectors-dialog.component.css']
})
export class BulkImportCollectorsDialogComponent {
  @ViewChild('fileInput', { static: false })
  fileInput: ElementRef<HTMLInputElement>;

  mode: BulkImportCollectorsMode = 'emails';
  inputValue = '';
  loading = false;
  error: string | null = null;
  selectedFile: File | null = null;
  fileName: string | null = null;
  emailResult: BulkImportCollectorsResult | null = null;
  csvPreviewResult: BulkImportCollectorsCsvPreviewResult | null = null;
  csvResult: BulkImportCollectorsCsvResult | null = null;
  validationErrors: BulkImportCollectorsCsvInvalidRow[] = [];
  importCompleted = false;

  constructor(
    private dialogRef: MatDialogRef<BulkImportCollectorsDialogComponent>,
    private collectorProfileService: CollectorProfileService,
    private alertService: AlertService,
    @Inject(MAT_DIALOG_DATA)
    private data: BulkImportCollectorsDialogData
  ) {}

  cancel() {
    this.dialogRef.close(this.importCompleted ? { refreshed: true } : null);
  }

  onModeChanged() {
    this.error = null;
    this.emailResult = null;
    this.csvPreviewResult = null;
    this.csvResult = null;
    this.validationErrors = [];
  }

  onFileSelected(event: Event) {
    if (this.loading) {
      return;
    }
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    this.selectedFile = input.files[0];
    this.fileName = this.selectedFile.name;
    this.error = null;
    this.csvPreviewResult = null;
    this.csvResult = null;
    this.validationErrors = [];
  }

  clearSelectedFile() {
    this.selectedFile = null;
    this.fileName = null;
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  downloadTemplate() {
    const blob = new Blob([this.csvTemplate], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'collectors_import_template.csv';
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  async importCollectors() {
    if (this.mode === 'csv') {
      if (this.csvPreviewResult) {
        await this.confirmCsvImport();
        return;
      }
      await this.previewCsv();
      return;
    }
    await this.importEmailList();
  }

  get parsedEmailsCount() {
    return this.parseEmails(this.inputValue).length;
  }

  get canImport() {
    return this.mode === 'csv'
      ? !!this.selectedFile && !this.loading
      : this.parsedEmailsCount > 0 && !this.loading;
  }

  get importActionLabel() {
    if (this.mode !== 'csv') {
      return 'Import';
    }
    return this.csvPreviewResult ? 'Confirm Import' : 'Preview';
  }

  get importActionIcon() {
    if (this.mode !== 'csv') {
      return 'cloud_upload';
    }
    return this.csvPreviewResult ? 'check' : 'preview';
  }

  get csvTemplate() {
    const eventSkillColumns = this.csvEventSkillColumns;
    const columns = [
      'email',
      'Discord Alias',
      'TEAM',
      'Role',
      'Reliability',
      ...eventSkillColumns,
      ...(this.data?.config?.eventCombinationFields ?? [])
    ];
    const sampleRow = [
      'collector@example.com',
      'discord-alias',
      'Team A',
      'Collector',
      'NONE',
      ...eventSkillColumns.map(() => 'NONE'),
      ...(this.data?.config?.eventCombinationFields ?? []).map(() => 'false')
    ];
    return `${columns.join(',')}\n${sampleRow.join(',')}`;
  }

  private get csvEventSkillColumns() {
    const eventSkillGroups = this.data?.config?.eventSkillGroups ?? [];
    if (eventSkillGroups.length > 0) {
      return eventSkillGroups.map((group) => group.label);
    }
    return this.data?.config?.eventSkillCodes ?? [];
  }

  private async importEmailList() {
    const emails = this.parseEmails(this.inputValue);
    if (emails.length === 0) {
      return;
    }

    this.loading = true;
    this.error = null;
    this.emailResult = null;
    try {
      this.emailResult = await firstValueFrom(
        this.collectorProfileService.bulkImportCollectorsByEmails(emails)
      );
      this.importCompleted = true;
      this.alertService.showInfo('Collector import finished.');
    } catch (error) {
      this.error = this.getErrorMessage(error, 'Could not import collectors');
      this.alertService.showError(this.error);
    } finally {
      this.loading = false;
    }
  }

  formatImportedAliasCollision(
    collision: BulkImportCollectorsCsvAliasCollision
  ): string {
    return (collision.imported ?? [])
      .map((entry) => entry.email || entry.auth0Id || 'Unknown')
      .join(', ');
  }

  formatExistingAliasCollision(
    collision: BulkImportCollectorsCsvAliasCollision
  ): string {
    return (collision.existing ?? [])
      .map((entry) =>
        entry.discordAlias && entry.auth0Id
          ? `${entry.discordAlias} (${entry.auth0Id})`
          : entry.discordAlias || entry.auth0Id || 'Unknown'
      )
      .join(', ');
  }

  private async previewCsv() {
    if (!this.selectedFile) {
      this.error = 'No CSV file selected';
      return;
    }

    this.loading = true;
    this.error = null;
    this.csvPreviewResult = null;
    this.csvResult = null;
    this.validationErrors = [];

    try {
      this.csvPreviewResult = await firstValueFrom(
        this.collectorProfileService.previewBulkImportCollectorsCsv(
          this.selectedFile
        )
      );
      this.validationErrors = this.csvPreviewResult.invalid ?? [];
      this.alertService.showInfo('Collector CSV preview ready.');
    } catch (error) {
      this.validationErrors = error?.error?.errors ?? [];
      this.error = this.getErrorMessage(error, 'Could not preview CSV');
      this.alertService.showError(this.error);
    } finally {
      this.loading = false;
    }
  }

  private async confirmCsvImport() {
    if (!this.selectedFile) {
      this.error = 'No CSV file selected';
      return;
    }

    this.loading = true;
    this.error = null;
    this.csvResult = null;
    this.validationErrors = [];

    try {
      this.csvResult = await firstValueFrom(
        this.collectorProfileService.bulkImportCollectorsCsv(this.selectedFile)
      );
      this.validationErrors = this.csvResult.invalid ?? [];
      this.csvPreviewResult = null;
      this.importCompleted = true;
      this.clearSelectedFile();
      this.alertService.showInfo('Collector CSV import finished.');
    } catch (error) {
      this.validationErrors = error?.error?.errors ?? [];
      this.error = this.getErrorMessage(error, 'Could not import CSV');
      this.alertService.showError(this.error);
    } finally {
      this.loading = false;
    }
  }

  private parseEmails(inputValue: string): string[] {
    return [
      ...new Set(
        (inputValue ?? '')
          .split(/[,\n;]+/g)
          .map((value) => value.trim().toLowerCase())
          .filter((value) => value.length > 0)
      )
    ];
  }

  private getErrorMessage(error: any, fallback: string): string {
    return error?.error?.message ?? error?.message ?? fallback;
  }
}
